// Video operations API
import { getAPI, token } from "@/api/params.js";
import { MEDIA_UPDATE_INTERVAL, MEDIA_CONVERSION_STATUS } from "@/constants.js";

// Key that uses to stop gifs updatings
let gifsIntervalKey = null;

// Function, that handles new GIFs list
let gifsUpdateCallback = null;
// Function, that executes when error occured
let gifsErrorCallback = null;

/**
 * Get server-format GIF data and convert to client-format
 * @param {Object} itemData server-format cGIF data
 * @returns client-format GIF data
 */
function parseGif(itemData) {
  return {
    id: itemData.id,
    status: itemData.status ?? MEDIA_CONVERSION_STATUS.READY,
    link: itemData.storageUrl + "/" + itemData.link ?? null
  };
}

/**
 * Request GIFs list from server
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
async function getGIFs(app, stream) {
  let withoutStream = false;
  if (stream === null) {
    withoutStream = true;
    stream = "";
  }
  const gifsListRequest = `${getAPI()}/gif?app=${app}&stream=${stream}&token=${token}${
    withoutStream ? "&only_without_stream=true" : ""
  }`;
  const gifsResponse = await fetch(gifsListRequest, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token
    }
  });
  if (!gifsResponse.ok) {
    return {
      success: false,
      error: `${gifsResponse.status}: ${gifsResponse.statusText}`
    };
  }
  const gifsObj = await gifsResponse.json();
  return {
    success: true,
    items: gifsObj
  };
}

/**
 * Get GIFs and execute callback function or error handler (if getting has failed)
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
async function updateGIFs(app, stream) {
  const gifs = await getGIFs(app, stream);
  if (gifs.success) {
    gifsUpdateCallback(gifs.items.map(parseGif));
  } else {
    gifsErrorCallback(gifs.error);
  }
}

/**
 * Execute callback function when GIFs list updated
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @param {Function} callback Callback function with one argument (gifs list)
 * @param {Function} error Error function with one argument (error message)
 */
export const subscribeToGIFUpdates = (app, stream, callback, error) => {
  if (gifsIntervalKey != null) {
    unsubscribeFromGIFUpdates();
  }
  if (stream === "No Stream") {
    stream = "";
  }
  gifsUpdateCallback = callback;
  gifsErrorCallback = error;

  updateGIFs(app, stream);
  gifsIntervalKey = setInterval(
    () => updateGIFs(app, stream),
    MEDIA_UPDATE_INTERVAL
  );
};

/**
 * Cancel callback binding to GIFs updating
 */
export const unsubscribeFromGIFUpdates = () => {
  clearInterval(gifsIntervalKey);
  gifsIntervalKey = null;
};

/**
 * Create GIF video from short video
 * @param {Number} videoId Video ID
 * @param {Object} gifParams Converting parameters:
 *      startOffset (Integer) - start time in seconds, must be more than 0
 *      duration (Integer) - GIF duration in seconds
 *      scaleWidth (Integer) - GIF width in pixels
 *      inFps (String) - input video FPS
 *      outputFps (String) - GIF FPS
 *      looped (Boolean) - looping GIF flag
 *      frameLimit (Integer) - max frame number
 *      mode (String) - always 'slides'
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const generateGIF = async (videoId, gifParams, app, stream) => {
  const generateURL = `${getAPI()}/convert/${videoId}/gif?token=${token}`;
  const body = JSON.stringify(gifParams);

  const resp = await fetch(generateURL, {
    method: "POST",
    body: body,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token
    }
  });

  if (!resp.ok) {
    const { error } = await resp.json();
    throw Error(error);
  }

  updateGIFs(app, stream);
};

/**
 * Remove GIF video
 * @param {Number} id GIF ID
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const removeGIF = async (id, app, stream) => {
  const removeURL = `${getAPI()}/gif/${id}?token=${token}`;
  const resp = await fetch(removeURL, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token
    }
  });

  if (!resp.ok) {
    const { error } = await resp.json();
    throw Error(error);
  }

  updateGIFs(app, stream);
};

/**
 * Remove all GIF clips
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const removeAllGIFs = async (app, stream) => {
  if (stream === "No Stream") {
    stream = "";
  }
  let withoutStream = false;
  if (stream === null) {
    withoutStream = true;
    stream = "";
  }
  const removeURL = `${getAPI()}/gif?token=${token}&app=${app}&stream=${stream}${
    withoutStream ? "&only_without_stream=true" : ""
  }`;
  const resp = await fetch(removeURL, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token
    }
  });

  if (!resp.ok) {
    const { error } = await resp.json();
    throw Error(error);
  }
  stream = null;
  updateGIFs(app, stream);
};
